import classNames from "classnames"
import React, { ChangeEvent, CSSProperties, useCallback } from "react"

interface RangeSliderProps {
    min?: number
    max?: number
    step?: number
    // New required prop for controlled state
    value: { min: number; max: number }
    onChange: (values: { min: number; max: number }) => void
    selectionStyle?: CSSProperties
    trackStyle?: CSSProperties
}

const BASE_RANGE_STYLES = `
  absolute w-full h-full inset-0
  appearance-none bg-transparent pointer-events-none
  [&::-webkit-slider-thumb]:appearance-none
  [&::-webkit-slider-thumb]:pointer-events-auto
  [&::-moz-range-thumb]:appearance-none
  [&::-moz-range-thumb]:pointer-events-auto
`

const WEB_KIT_STYLES = `
  [&::-webkit-slider-thumb]:w-1
  [&::-webkit-slider-thumb]:h-9
  [&::-webkit-slider-thumb]:rounded-full
  [&::-webkit-slider-thumb]:bg-gray-50
  [&::-webkit-slider-thumb]:hover:bg-blue-500
  [&::-webkit-slider-thumb]:border
  [&::-webkit-slider-thumb]:border-gray-300
  [&::-webkit-slider-thumb]:cursor-grab
  [&::-webkit-slider-thumb]:active:scale-110
`

const MOZ_STYLES = `
  [&::-moz-range-thumb]:w-1
  [&::-moz-range-thumb]:h-9
  [&::-moz-range-thumb]:rounded-full
  [&::-moz-range-thumb]:bg-gray-50
  [&::-moz-range-thumb]:border
  [&::-moz-range-thumb]:border-gray-300
  [&::-moz-range-thumb]:cursor-grab
  [&::-moz-range-thumb]:active:scale-110
`

const THUMB_STYLES = classNames(BASE_RANGE_STYLES, WEB_KIT_STYLES, MOZ_STYLES)

const RangeSlider: React.FC<RangeSliderProps> = ({
    min = 0,
    max = 100,
    step = 1,
    value, // Destructure value prop
    onChange,
    selectionStyle = {},
    trackStyle = {},
}) => {
    // No local state (useState) or effects (useEffect) needed anymore.

    const handleMinChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newMinVal = Math.min(Number(e.target.value), value.max - step)
        onChange({ min: newMinVal, max: value.max })
    }

    const handleMaxChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newMaxVal = Math.max(Number(e.target.value), value.min + step)
        onChange({ min: value.min, max: newMaxVal })
    }

    const getPercent = useCallback(
        (val: number) => Math.round(((val - min) / (max - min)) * 100),
        [min, max]
    )

    return (
        <div className="relative w-full max-w-md h-10 flex items-center justify-center">
            <div
                className="absolute w-full h-2/3 rounded-md overflow-hidden border border-gray-600"
                style={trackStyle}
            >
                <div
                    className="absolute h-full"
                    style={{
                        left: `${getPercent(value.min)}%`,
                        width: `${getPercent(value.max) - getPercent(value.min)}%`,

                        // Shade affect over "unselected" portion
                        backgroundColor: "transparent",
                        boxShadow: "0 0 0 100vmax rgba(0, 0, 0, 0.5)",

                        ...selectionStyle,
                    }}
                ></div>
            </div>

            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value.min}
                onChange={handleMinChange}
                className={classNames(THUMB_STYLES, "z-20")}
                style={{
                    zIndex: value.min > max - 10 ? 25 : 20,
                }}
            />

            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value.max}
                onChange={handleMaxChange}
                className={classNames(THUMB_STYLES, "z-10")}
            />
        </div>
    )
}

export default RangeSlider
